import { defineStore, acceptHMRUpdate } from 'pinia'
import type {SiteSettingsData} from '~/store/siteSettings'

export interface USP {
  AltText: string,
  Code: string,
  Conditional: string,
  IconPath: string,
  PopupText: string,
  Title: string
}

export interface News {
  AltText: string,
  BrowserTitle: string,
  Code: string,
  DateEnd: string,
  DateStart: string,
  DownloadDescription: string,
  DownloadLanguage: string,
  DownloadNumberOfPages: string,
  DownloadPath: string,
  DownloadSize: string,
  DownloadTitle: string,
  FormCode: string,
  FormEmail: string,
  HeaderImage: string,
  HeaderImageDescription: string,
  HeaderImageTitle: string,
  HeaderType: string,
  LanguageCode: string,
  MainText: string,
  MetaDescription: string,
  MetaKeywords: string,
  MetaTitle: string,
  NameMenu: string,
  NameUrl: string,
  Priority: string,
  SocialMediaDescription: string,
  SocialMediaTitle: string,
  ThumbnailAltText: string,
  ThumbnailPath: string,
  Title: string,
  Usp: USP[]
  // Selector?? zie News.json schema
}

export interface Vacancy {
  AltText: string,
  BrowserTitle: string,
  Code: string,
  DateEnd: string,
  DateStart: string,
  DownloadDescription: string,
  DownloadLanguage: string,
  DownloadNumberOfPages: string,
  DownloadPath: string,
  DownloadSize: string,
  DownloadTitle: string,
  FormCode: string,
  FormEmail: string,
  HeaderImage: string,
  HeaderImageDescription: string,
  HeaderImageTitle: string,
  HeaderType: string,
  LanguageCode: string,
  MainText: string,
  MetaDescription: string,
  MetaKeywords: string,
  MetaTitle: string,
  NameMenu: string,
  NameUrl: string,
  Priority: string,
  ThumbnailAltText: string,
  ThumbnailPath: string,
  Title: string,
  Usp: string,
  WorkPlace: string
}

export interface EventDateTime {
  Date: string,
  TimeEnd: string,
  TimeStart: string
}
export interface Events {
  AltText: string,
  BrowserTitle: string,
  Code: string,
  Country: string,
  DownloadDescription: string,
  DownloadLanguage: string,
  DownloadNumberOfPages: string,
  DownloadPath: string,
  DownloadSize: string,
  DownloadTitle: string,
  EventDateTime: EventDateTime[],
  EventOrganisationName: string,
  FormCode: string,
  FormEmail: string,
  HeaderImage: string,
  HeaderImageDescription: string,
  HeaderImageTitle: string,
  HeaderType: string,
  ItemImage: string,
  LanguageCode: string,
  Latitude: string,
  LocationName: string,
  Longitude: string,
  MainText: string,
  MetaDescription: string,
  MetaKeywords: string,
  MetaTitle: string,
  NameMenu: string,
  NameUrl: string,
  SiteUrl: string,
  StandNumber: string,
  StreetHouseNumber: string,
  ThumbnailAltText: string,
  ThumbnailPath: string,
  Title: string,
  UrlFreeTickets: string,
  UrlVisitor: string,
  Usp: USP[],
  ZipcodeCity: string
}

export interface Actions {
  AltText: string,
  BrowserTitle: string,
  Code: string,
  DateEnd: string,
  DateStart: string,
  DownloadDescription: string,
  DownloadLanguage: string,
  DownloadNumberOfPages: string,
  DownloadPath: string,
  DownloadSize: string,
  DownloadTitle: string,
  FormCode: string,
  FormEmail: string,
  HeaderImage: string,
  HeaderImageDescription: string,
  HeaderImageTitle: string,
  HeaderType: string,
  LanguageCode: string,
  MainText: string,
  MetaDescription: string,
  MetaKeywords: string,
  MetaTitle: string,
  NameMenu: string,
  NameUrl: string,
  Priority: string,
  SocialMediaDescription: string,
  SocialMediaTitle: string,
  Title: string,
  ThumbnailAltText: string,
  ThumbnailPath: string,
  Width: string,
  Height: string,
  Usp: USP[]
}

export interface Reference {
  AltText: string,
  BrowserTitle: string,
  Code: string,
  DateEnd: string,
  DateStart: string,
  DownloadTitle: string,
  DownloadDescription: string,
  DownloadLanguage: string,
  DownloadNumberOfPages: string,
  DownloadPath: string,
  DownloadSize: string,
  FormCode: string,
  FormEmail: string,
  HeaderImage: string,
  HeaderImageDescription: string,
  HeaderImageTitle: string,
  HeaderType: string,
  LanguageCode: string,
  MainText: string,
  MetaDescription: string,
  MetaKeywords: string,
  MetaTitle: string,
  NameMenu: string,
  NameUrl: string,
  Priority: string,
  SocialMediaDescription: string,
  SocialMediaTitle: string,
  Title: string,
  ThumbnailAltText: string,
  ThumbnailPath: string,
  Usp: USP[]
}

export interface SelectorHeader {
  AttribuutSelectorName: string,
  Help: string,
  Name: string,
  Number: number,
  Popup: string,
  Symbol: string,
  Unit: string,
}

export interface SelectorSelectors {
  AttribuutSelectorName: string,
  DefaultValue: string,
  Number: number,
  SelectorName: string,
  SelectorType: string
}

export interface Selector {
  Header: SelectorHeader[]
  Products: string[],
  Selectors: SelectorSelectors[]
}

export interface Header {
  AltText: string,
  HeaderButton: any[],
  HeaderImage: string,
  HeaderImageDescription: string,
  HeaderImageTitle: string
}

export interface Page {
  Articles: string,
  BrowserTitle: string,
  Code: string,
  Download: any[],
  Form: string,
  GoogleIndex: string,
  Header: Header[],
  HeaderType: string,
  MainText: string,
  MetaDescription: string,
  MetaKeywords: string,
  MetaTitle: string,
  NameMenu: string,
  NameUrl: string,
  PageFormEmail: string,
  PageType: string,
  Selector: Selector[],
  ShortText: string,
  Title: string,
  TitleShow: string,
  Usp: string
}

export interface IndexPage extends SiteSettingsData {
  MetaDescription: string,
  MetaTitle: string,
  MetaKeywords: string,
  BrowserTitle: string,
  Title: string,
  NameUrl: string,
  NameMenu: string,
  PageType: string
}

export const useCurrentPageStore = defineStore('currentPage', {
  state: () => ({
    page: null as Page | null,
    news: null as News | null,
    vacancy: null as Vacancy | null,
    events: null as Events | null,
    actions: null as Actions | null,
    reference: null as Reference | null,
    indexPage: null as IndexPage | null,
    type: '',
    initialized: false,
    loading: false
  }),
  
  getters: {
    getPage: (state) => {
      try {
        switch(state.type) {
          case 'news':
            return state.news
          case 'vacancy':
            return state.vacancy
          case 'events':
          case 'event':
            return state.events
          case 'actions':
          case 'action':
            return state.actions
          case 'reference':
            return state.reference
          case 'indexPage':
            return state.indexPage
          default:
            return state.page
        }
      }
      catch (e) {
        console.error('error when getting Page:', e);
      }
    },

    getPageCode: (state) => {
      switch(state.type) {
        case 'news':
          return state.news?.Code
        case 'vacancy':
          return state.vacancy?.Code
        case 'events':
        case 'event':
          return state.events?.Code
        case 'actions':
        case 'action':
          return state.actions?.Code
        case 'reference':
          return state.reference?.Code
        case 'indexPage':
          return state.indexPage?.Code
        default:
          return state.page?.Code
      }
    },

    getType: (state) => {
      return state.type;
    }
  },

  actions: {
    setInitialized(value: boolean) {
      this.initialized = value;
    },

    setLoading(value: boolean) {
      this.loading = value;
    },

    populate(pageData: any, currentType: string) {
      this.setType(currentType);
      const type = this.getType;
      
      switch(type) {
        case 'news':
          this.news = pageData
          break;
        case 'vacancy':
          this.vacancy = pageData
          break;
        case 'events':
        case 'event':
          this.events = pageData
          break;
        case 'actions':
        case 'action':
          this.actions = pageData
          break;
        case 'reference':
          this.reference = pageData
          break;
        case 'indexPage':
          this.indexPage = pageData
          break;
        default:
          this.page = pageData
      }
      this.setInitialized(true);
    },

    setType(type: string) {
      this.type = type.toLowerCase();
    },

    reset() {
      this.$reset()
      this.initialized = false
      this.loading = false
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCurrentPageStore, import.meta.hot))
}